import { useEffect, useState } from 'react'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useHistory, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

import http from '../../../../../../shared/http'
import { TApi, TRoutes } from '../../../../../../shared/const'
import { STATUS_LABELS } from '../../../../admin/Courses/labels'

import config from '../RequirementsConfig/style.module.scss'
import own from './style.module.scss'

// Wie die Einstellung „Skills4Retail-Konzept" ihre Sperre je Form nennt — kurz.
const GATE_LABELS = {
	OFF: 'off',
	ACTIVATION: 'before activation',
	FIRST_REPORT: 'before the first report'
}

/** Kurzfassung der Skills4Retail-Einstellung: „JC: vor der Aktivierung · JCC: aus …" */
export const conceptSummary = (value, t = text => text) => {
	if (!value || value.active === false) return t('switched off')
	const gates = value.gates || {}
	return ['JC', 'JCC', 'JBC'].map(typ => `${typ}: ${t(GATE_LABELS[gates[typ]] || GATE_LABELS.OFF)}`).join(' · ')
}

/**
 * Welche Kurse es gibt — mit Namen und eigenem Schalter.
 *
 * Bis 31.10.2026 stand hier nur „Kurse eingeschaltet", und welcher Kurs damit
 * gemeint war, sah man erst in der Kursverwaltung. Jetzt steht jeder Kurs mit
 * Namen, Unternehmensformen und Stand in der Liste; der Schalter
 * veröffentlicht ihn bzw. nimmt ihn als Entwurf zurück — derselbe Wert wie im
 * Reiter „Grunddaten" der Kursverwaltung, kein zweiter daneben. Archivierte
 * Kurse stehen nur dort.
 *
 * Skills4Retail steht als eigene Zeile darunter, weil es für die Schulen
 * dieselbe Sache ist: etwas, das sie lernen und abschließen. Geschaltet wird
 * es aber in seiner eigenen Einstellung — dort entscheidet sich je
 * Unternehmensform, ob das Konzept die Aktivierung oder den ersten Bericht
 * sperrt. Ein zweiter Schalter hier liefe mit ihr auseinander; die Zeile zeigt
 * deshalb den Stand und springt dorthin.
 */
export default function CourseCatalog({ concept, enabled }) {
	const { t } = useTranslation()
	const history = useHistory()
	const { pathname } = useLocation()
	const [kurse, setKurse] = useState(null)
	const [schaltet, setSchaltet] = useState(null)

	useEffect(() => {
		let aktiv = true
		http.get(TApi.ADMIN_COURSES, { disableToast: true })
			.then(({ data }) => aktiv && setKurse(Array.isArray(data) ? data : data?.rows || []))
			.catch(() => aktiv && setKurse([]))
		return () => {
			aktiv = false
		}
	}, [])

	const umschalten = kurs => {
		const status = kurs.status === 'PUBLISHED' ? 'DRAFT' : 'PUBLISHED'
		setSchaltet(kurs.id)
		http.put(TApi.ADMIN_COURSE(kurs.id), { status })
			.then(() => {
				setKurse(liste => liste.map(k => (k.id === kurs.id ? { ...k, status } : k)))
				toast.success(status === 'PUBLISHED' ? t('Course published') : t('Course set back to draft'))
			})
			.finally(() => setSchaltet(null))
	}

	const sichtbar = (kurse || []).filter(k => k.status !== 'ARCHIVED')

	return (
		<div className={own.katalog}>
			<h4 className={own.katalogTitel}>{t('Courses for students')}</h4>
			{!enabled && (
				<p className={own.katalogHinweis}>
					{t('As long as courses are switched off above, no course is shown — even a published one.')}
				</p>
			)}

			{kurse === null && <p className={own.katalogHinweis}>{t('Loading…')}</p>}

			<ul className={own.katalogListe}>
				{sichtbar.map(kurs => (
					<li key={kurs.id} className={own.katalogZeile}>
						<label className={clsx(config.switch, own.katalogSchalter)}>
							<input
								type="checkbox"
								checked={kurs.status === 'PUBLISHED'}
								disabled={schaltet === kurs.id}
								onChange={() => umschalten(kurs)}
								aria-label={`${kurs.title}: ${t('published')}`}
							/>
							<span className={config.track} aria-hidden="true" />
						</label>
						<div className={own.katalogText}>
							<button
								type="button"
								className={own.katalogName}
								onClick={() => history.push(TRoutes.ADMIN_COURSE(kurs.id))}
							>
								{kurs.title}
							</button>
							<span className={own.katalogMeta}>
								{t(STATUS_LABELS[kurs.status] || kurs.status)}
								{' · '}
								{kurs.companyTypes?.length ? kurs.companyTypes.join(', ') : t('no company type yet')}
								{typeof kurs.completionCount === 'number' &&
									` · ${t('Completions: {{n}}', { n: kurs.completionCount })}`}
							</span>
						</div>
					</li>
				))}

				<li className={clsx(own.katalogZeile, own.katalogFremd)}>
					<span className={own.katalogMarke} aria-hidden="true">
						S4R
					</span>
					<div className={own.katalogText}>
						<button
							type="button"
							className={own.katalogName}
							onClick={() => history.push(`${pathname}#conceptRequirement`)}
						>
							{t('Skills4Retail learning path')}
						</button>
						<span className={own.katalogMeta}>
							{conceptSummary(concept, t)} · {t('switched in its own setting')}
						</span>
					</div>
				</li>
			</ul>

			<button
				type="button"
				className={own.katalogNeu}
				onClick={() => history.push(`${TRoutes.ADMIN_COURSES}?neu=1`)}
			>
				+ {t('Create course')}
			</button>
		</div>
	)
}

CourseCatalog.propTypes = {
	concept: PropTypes.object,
	enabled: PropTypes.bool
}
